import { useEffect, useState } from "react";
import {
  Card, Table, Button, Tag, Upload, Modal, Select, Input, Space,
  Popconfirm, Typography, Row, Col, Badge, Empty
} from "antd";
import {
  UploadOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined,
  FileWordOutlined, FilePdfOutlined, EyeOutlined, CloudUploadOutlined,
  CheckCircleOutlined, SearchOutlined, EditOutlined, SaveOutlined
} from "@ant-design/icons";
import { useNotification } from "../../../utils/notificationProvider";
import { appCRUD } from "../../../actions/app";
import { selector } from "../../../api";
import { char } from "../../../utils";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export const UploadedTimetableContent = () => {
  const user = selector.User();
  const openNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [index, setIndex] = useState(0);

  // Grid view/edit modal
  const [gridOpen, setGridOpen] = useState(false);
  const [gridData, setGridData] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [gridDay, setGridDay] = useState("Monday");

  // Upload modal
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    title: "", document_type: "TEACHING", cumpas: null,
    academic: null, semister: null, description: "",
  });

  // Dropdowns
  const [campuses, setCampuses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);

  // Load data
  useEffect(() => {
    setLoading(true);
    appCRUD
      .loadUploadedTimetables(pageSize, (page - 1) * pageSize, search, "-recorded_at")
      .then((resp) => {
        setData(resp.data.results || []);
        setCount(resp.data.count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, pageSize, search, index]);

  // Load dropdowns
  useEffect(() => {
    appCRUD.loadCampas(50, 0, "", "name").then((r) => setCampuses(r.data.results || [])).catch(() => {});
    appCRUD.loadAcademic(50, 0, "", "-id").then((r) => setAcademicYears(r.data.results || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (uploadForm.academic) {
      const year = academicYears.find((y) => y.id === uploadForm.academic);
      if (year) {
        appCRUD.loadSemister(50, 0, year.id, "", "-id")
          .then((r) => setSemesters(r.data.results || []))
          .catch(() => setSemesters([]));
      }
    } else {
      setSemesters([]);
    }
  }, [uploadForm.academic, academicYears]);

  const loadGrid = (timetable) => {
    setSelectedTimetable(timetable);
    setGridOpen(true);
    setGridLoading(true);
    appCRUD.loadUploadedEntries(timetable.id)
      .then((r) => setGridData(r.data))
      .catch(() => setGridData(null))
      .finally(() => setGridLoading(false));
  };

  const handleEntryUpdate = (entryId, field, value) => {
    appCRUD.updateUploadedEntry(entryId, { [field]: value })
      .then(() => {
        openNotification("success", "Updated", `Entry ${field} updated`);
        loadGrid(selectedTimetable);
      })
      .catch(() => openNotification("error", "Error", "Failed to update"));
  };

  const handleEntryDelete = (entryId) => {
    appCRUD.deleteUploadedEntry(entryId)
      .then(() => {
        openNotification("success", "Deleted", "Entry removed");
        loadGrid(selectedTimetable);
      })
      .catch(() => openNotification("error", "Error", "Failed to delete"));
  };

  const handleUpload = () => {
    if (!fileList.length) return openNotification("error", "Error", "Please select a file");
    if (!uploadForm.title) return openNotification("error", "Error", "Title is required");
    if (!uploadForm.cumpas) return openNotification("error", "Error", "Campus is required");
    if (!uploadForm.academic) return openNotification("error", "Error", "Academic Year is required");
    if (!uploadForm.semister) return openNotification("error", "Error", "Semester is required");

    const formData = new FormData();
    formData.append("file", fileList[0].originFileObj || fileList[0]);
    formData.append("title", uploadForm.title);
    formData.append("document_type", uploadForm.document_type);
    formData.append("cumpas", uploadForm.cumpas);
    formData.append("academic", uploadForm.academic);
    formData.append("semister", uploadForm.semister);
    formData.append("description", uploadForm.description || "");

    setUploading(true);
    appCRUD
      .uploadTimetable(formData)
      .then(() => {
        openNotification("success", "Success", "Timetable uploaded successfully!");
        setUploadOpen(false);
        setFileList([]);
        setUploadForm({ title: "", document_type: "TEACHING", cumpas: null, academic: null, semister: null, description: "" });
        setIndex(index + 1);
      })
      .catch((err) => {
        openNotification("error", "Error", err.response?.data?.error || "Upload failed");
      })
      .finally(() => setUploading(false));
  };

  const handleDelete = (id) => {
    appCRUD.deleteUploadedTimetable(id)
      .then(() => {
        openNotification("success", "Deleted", "Timetable removed");
        setIndex(index + 1);
      })
      .catch(() => openNotification("error", "Error", "Failed to delete"));
  };

  const handlePublish = (id, published) => {
    appCRUD.updateUploadedTimetable(id, { is_published: !published })
      .then(() => {
        openNotification("success", "Updated", published ? "Unpublished" : "Published to Ratiba");
        setIndex(index + 1);
      })
      .catch(() => openNotification("error", "Error", "Failed to update"));
  };

  const getFileIcon = (name) => {
    if (!name) return <UploadOutlined />;
    if (name.endsWith(".pdf")) return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    return <FileWordOutlined style={{ color: "#1890ff" }} />;
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const columns = [
    {
      title: "#", key: "sn", width: 50,
      render: (_, __, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: "Document", key: "doc", width: 300,
      render: (_, record) => (
        <Space>
          {getFileIcon(record.file_name)}
          <div>
            <Text strong>{record.title}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.file_name} ({formatSize(record.file_size)})
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Type", dataIndex: "document_type", key: "type", width: 120,
      render: (t) => (
        <Tag color={t === "TEACHING" ? "blue" : t === "EXAM" ? "red" : "default"}>
          {t}
        </Tag>
      ),
    },
    {
      title: "Campus", key: "campus", width: 120,
      render: (_, r) => char.toTitleCase(r.cumpas?.name || ""),
    },
    {
      title: "Academic Year", key: "academic", width: 120,
      render: (_, r) => r.academic?.name || "",
    },
    {
      title: "Semester", key: "semester", width: 120,
      render: (_, r) => char.toTitleCase(r.semister?.name || ""),
    },
    {
      title: "Status", key: "status", width: 100,
      render: (_, r) => (
        <Tag color={r.is_published ? "success" : "warning"}>
          {r.is_published ? "Published" : "Draft"}
        </Tag>
      ),
    },
    {
      title: "Actions", key: "actions", width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button size="small" type="primary" icon={<EyeOutlined />}
            onClick={() => loadGrid(record)}>
            View / Edit
          </Button>
          <Button size="small" icon={<DownloadOutlined />}
            onClick={() => window.open(appCRUD.getUploadedTimetableDownloadUrl(record.id), "_blank")}>
            Download
          </Button>
          <Button size="small" type={record.is_published ? "default" : "primary"}
            icon={<CheckCircleOutlined />}
            onClick={() => handlePublish(record.id, record.is_published)}>
            {record.is_published ? "Unpublish" : "Publish"}
          </Button>
          <Popconfirm title="Delete this timetable?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="container-fluid">
      <Row className="mb-3" justify="space-between" align="middle">
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <CloudUploadOutlined className="me-2" />
            Uploaded Timetables
          </Title>
          <Text type="secondary">Upload and manage Word/PDF timetable documents</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadOpen(true)}>
            Upload Timetable
          </Button>
        </Col>
      </Row>
      <hr />

      <Card>
        <div className="mb-3">
          <Input.Search
            placeholder="Search by title or file name..."
            allowClear
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 350 }}
            prefix={<SearchOutlined />}
          />
        </div>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey="id"
          size="small"
          bordered
          pagination={{
            current: page, pageSize, total: count,
            onChange: (p, s) => { setPage(p); setPageSize(s); },
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} documents`,
          }}
        />
      </Card>

      {/* Grid View/Edit Modal */}
      <Modal
        title={selectedTimetable ? `${selectedTimetable.title} - Grid View` : "Timetable Grid"}
        open={gridOpen}
        onCancel={() => setGridOpen(false)}
        footer={null}
        width="95%"
        style={{ top: 20 }}
      >
        {gridLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Typography.Text>Loading entries...</Typography.Text></div>
        ) : !gridData || gridData.total === 0 ? (
          <Empty description="No parsed entries. Only .docx files are auto-parsed." />
        ) : (
          <>
            <Row gutter={12} className="mb-3" align="middle">
              <Col>
                <Select value={gridDay} onChange={setGridDay} style={{ width: 160 }}>
                  {DAY_ORDER.map((d) => (
                    <Option key={d} value={d}>{d} ({(gridData.grouped[d] || []).length})</Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Badge count={gridData.total} style={{ backgroundColor: "#1890ff" }}>
                  <Tag>Total Entries</Tag>
                </Badge>
              </Col>
              <Col>
                <Tag color="blue">{gridData.rooms?.length || 0} Rooms</Tag>
              </Col>
            </Row>

            <Table
              dataSource={gridData.grouped[gridDay] || []}
              rowKey="id"
              size="small"
              bordered
              pagination={false}
              scroll={{ x: 800, y: 500 }}
              columns={[
                {
                  title: "Room", dataIndex: "room", key: "room", width: 100, fixed: "left",
                  render: (text, record) => (
                    editingEntry === record.id ? (
                      <Input size="small" defaultValue={text}
                        onPressEnter={(e) => { handleEntryUpdate(record.id, "room", e.target.value); setEditingEntry(null); }}
                        onBlur={(e) => { handleEntryUpdate(record.id, "room", e.target.value); setEditingEntry(null); }} />
                    ) : <Tag>{text}</Tag>
                  ),
                },
                {
                  title: "Start", dataIndex: "start_time", key: "start", width: 80,
                  render: (t) => t?.substring(0, 5),
                },
                {
                  title: "End", dataIndex: "end_time", key: "end", width: 80,
                  render: (t) => t?.substring(0, 5),
                },
                {
                  title: "Course", dataIndex: "course", key: "course", width: 150,
                  render: (text, record) => (
                    editingEntry === record.id ? (
                      <Input size="small" defaultValue={text}
                        onPressEnter={(e) => { handleEntryUpdate(record.id, "course", e.target.value); setEditingEntry(null); }}
                        onBlur={(e) => { handleEntryUpdate(record.id, "course", e.target.value); setEditingEntry(null); }} />
                    ) : <strong>{text}</strong>
                  ),
                },
                {
                  title: "Actions", key: "actions", width: 100,
                  render: (_, record) => (
                    <Space size="small">
                      <Button size="small" icon={<EditOutlined />}
                        type={editingEntry === record.id ? "primary" : "default"}
                        onClick={() => setEditingEntry(editingEntry === record.id ? null : record.id)} />
                      <Popconfirm title="Delete?" onConfirm={() => handleEntryDelete(record.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* Upload Modal */}
      <Modal
        title={<span><CloudUploadOutlined className="me-2" />Upload Timetable Document</span>}
        open={uploadOpen}
        onCancel={() => { setUploadOpen(false); setFileList([]); }}
        onOk={handleUpload}
        confirmLoading={uploading}
        okText="Upload"
        width={600}
      >
        <div style={{ marginTop: 16 }}>
          <div className="mb-3">
            <label className="d-block mb-1 fw-bold">Title *</label>
            <Input placeholder="e.g. Teaching Timetable - Main Campus Sem 1"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} />
          </div>

          <Row gutter={12} className="mb-3">
            <Col span={12}>
              <label className="d-block mb-1 fw-bold">Document Type *</label>
              <Select style={{ width: "100%" }} value={uploadForm.document_type}
                onChange={(v) => setUploadForm({ ...uploadForm, document_type: v })}>
                <Option value="TEACHING">Teaching Timetable</Option>
                <Option value="EXAM">Examination Timetable</Option>
                <Option value="OTHER">Other Document</Option>
              </Select>
            </Col>
            <Col span={12}>
              <label className="d-block mb-1 fw-bold">Campus *</label>
              <Select style={{ width: "100%" }} placeholder="Select Campus" allowClear showSearch optionFilterProp="children"
                value={uploadForm.cumpas}
                onChange={(v) => setUploadForm({ ...uploadForm, cumpas: v })}>
                {campuses.map((c) => <Option key={c.id} value={c.id}>{char.toTitleCase(c.name)}</Option>)}
              </Select>
            </Col>
          </Row>

          <Row gutter={12} className="mb-3">
            <Col span={12}>
              <label className="d-block mb-1 fw-bold">Academic Year *</label>
              <Select style={{ width: "100%" }} placeholder="Academic Year" allowClear
                value={uploadForm.academic}
                onChange={(v) => setUploadForm({ ...uploadForm, academic: v, semister: null })}>
                {academicYears.map((y) => <Option key={y.id} value={y.id}>{y.name}</Option>)}
              </Select>
            </Col>
            <Col span={12}>
              <label className="d-block mb-1 fw-bold">Semester *</label>
              <Select style={{ width: "100%" }} placeholder={uploadForm.academic ? "Select Semester" : "Select Academic Year first"}
                disabled={!uploadForm.academic} allowClear
                value={uploadForm.semister}
                onChange={(v) => setUploadForm({ ...uploadForm, semister: v })}>
                {semesters.map((s) => <Option key={s.id} value={s.id}>{char.toTitleCase(s.name)}</Option>)}
              </Select>
            </Col>
          </Row>

          <div className="mb-3">
            <label className="d-block mb-1 fw-bold">Description</label>
            <TextArea rows={2} placeholder="Optional description..."
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
          </div>

          <div className="mb-3">
            <label className="d-block mb-1 fw-bold">File (Word/PDF) *</label>
            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList.slice(-1))}
              accept=".pdf,.doc,.docx"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </div>
        </div>
      </Modal>
    </div>
  );
};
